import React, { useState, useCallback, useRef, useEffect } from "react"
import cn from "classnames"
import SVGInline from "react-svg-inline"
import { useTranslation } from "react-i18next"
import { sizeUnit } from "@/common/locales"
import SignPreview, { useSignPreview, type SignPreviewOptions } from "@/common/components/sign_preview"
import { fixmethodName, type BasketItem } from "@/checkout/components/basket/items"
import { TrashFill, TrashOutline, EditFill, EditOutline, EyeOpenOutline, BundleOutline } from "@/skylticons"
import { debounce } from "lodash-es"
import ClientData from "@/common/client_data"
import { type FragmentType, getFragment, graphql } from "@/api"
import type { BasketItemFragment } from "@/api/graphql"
import { useMutation } from "@apollo/client"
import type { TFunction } from "i18next"

export const BasketItemFragmentDoc = graphql(`
  fragment BasketItem on OrderItem {
    id
    width
    height
    localPrice
    localTaxedPrice
    localTotal
    localTaxedTotal
    priceSek
    previewSvg
    previewSvgB
    articleType
    signType
    totalCount
    hangType
    description
    colors
    materialTr
    additionalProduct
    metaProduct
    clientDescription
    customerEdit
    regularLocalTotal
    regularTaxedTotal
    hasDiscount
    customProduct
    customProductAttributes
    bundleItem
    exampleFixedPrice

    bundle {
      id

      image {
        id
        webp
        png
      }
    }
  }
`)

const countUpdateDoc = graphql(`
  mutation BasketCountUpdate($id: ID!, $count: Int!) {
    basketCountUpdate(id: $id, count: $count) {
      success

      item {
        totalCount
        ...BasketItem
      }

      status {
        ...BasketModal_Order
      }
    }
  }
`)

const deleteDoc = graphql(`
  mutation BasketDelete($id: ID!) {
    basketDelete(id: $id) {
      success

      status {
        ...BasketModal_Order
      }
    }
  }
`)

const size = (item: BasketItemFragment) => {
  if (!item.width && !item.height) {
    return ""
  }

  return `${item.width || 0}x${item.height || 0}`
}

const editLink = (t: TFunction, item: BasketItemFragment) => {
  if (item.exampleFixedPrice) {
    if (confirm(t("fixed_price.dialog_text")) == true) {
      window.location.href = `/order/edit_id/${item.id}`
    }
  } else {
    window.location.href = `/order/edit_id/${item.id}`
  }
}

interface BasketSignItemProps {
  even?: boolean
  desktop: boolean
  scrollIntoView?: boolean
  index: number
  item: FragmentType<typeof BasketItemFragmentDoc>
}

interface BasketSignItemWrappedProps extends BasketSignItemProps {
  deleteItem: () => void
  loading: boolean
  countLoading: boolean
  count: string
  currentCount: number
  countOp: (op: "plus" | "minus") => void
  countChange: (value: string) => void
  rowRef: React.RefObject<HTMLDivElement & HTMLTableRowElement>
  openPreview: (options: SignPreviewOptions) => void
  signCode: boolean
  name?: string | null
  svgs: string[]
  editable: boolean
  bundleItem: boolean
}

const BasketSignItemMobile: React.FC<BasketSignItemWrappedProps> = ({
  item: itemFragment,
  deleteItem,
  loading,
  count,
  countChange,
  rowRef,
  openPreview,
  signCode,
  name,
  svgs,
  editable,
  bundleItem,
}) => {
  const item = getFragment(BasketItemFragmentDoc, itemFragment)
  const { t } = useTranslation()
  const switchBackground =
    (item.signType === "vinyl_text" || item.signType === "contour_cut") && item.colors === "White"
  const bundleSvg = bundleItem && item.previewSvg

  return (
    <div key={item.id} className={cn("item product-row mobile", { loading })} ref={rowRef}>
      <div className="details">
        {(signCode || bundleSvg) && (
          <div
            className="sign-wrapper"
            onClick={() =>
              openPreview({
                svg: svgs,
                transparent: true,
                withBackground: true,
                signWidth: item.width || 0,
                signHeight: item.height || 0,
                signType: item.signType || "",
                metaProduct: item.metaProduct || "",
              })
            }
          >
            <div className={cn("sign", { "bg-white-10": switchBackground, "bg-white-5": !switchBackground })}>
              {item.previewSvgB ? (
                <>
                  <SVGInline component="div" svg={item.previewSvg || ""} className="svg-wrapper data-hj-suppress" />
                  <SVGInline component="div" svg={item.previewSvgB || ""} className="svg-wrapper data-hj-suppress" />
                </>
              ) : (
                <SVGInline component="div" svg={item.previewSvg || ""} className="svg-wrapper data-hj-suppress" />
              )}
            </div>
            <div className="paragraph-2xs center mb-0">{t("signomatic.enlarge_img")}</div>
          </div>
        )}
        {bundleItem && !bundleSvg && (
          <div className="sign-wrapper">
            <div
              className={cn("sign flex justify-center", {
                "bg-white-10": switchBackground,
                "bg-white-5": !switchBackground,
              })}
            >
              {item.bundle?.image?.png ? <img src={item.bundle.image.png} /> : <BundleOutline className="h-16 w-16" />}
            </div>
          </div>
        )}
        {!(signCode || bundleItem) && (
          <div className={cn("sign", { "bg-white-10": switchBackground, "bg-white-5": !switchBackground })} />
        )}
        <div className="description">
          <div className="title">
            <div className="text-sm font-semibold">{name}</div>
            <div className="text-sm font-semibold">
              {ClientData.businessRole ? item.localTotal : item.localTaxedTotal}
            </div>
          </div>
          <div className="prop_count_row">
            <div className="properties">
              {item.previewSvgB && <div className="double-sided">{item.materialTr}</div>}
              <div className="text-xs">
                {signCode ? `${size(item)} ${sizeUnit}` : ""}
                {item.hangType && item.hangType !== "none" ? `, ${t(`hang_type.${item.hangType}`)}` : ""}
                {item.colors ? `, ${item.colors}` : ""}
                {parseInt(count) > 1
                  ? `, ${ClientData.businessRole ? item.localPrice : item.localTaxedPrice} ${t(
                      "signomatic.price_per_sign"
                    )}`
                  : ""}
              </div>
            </div>
            <div>
              {item.hasDiscount && (
                <div className="text-right text-xs text-ink-85 line-through">
                  {ClientData.businessRole ? item.regularLocalTotal : item.regularTaxedTotal}
                </div>
              )}
              <select
                id="basket_item_count"
                className="form-control article_count"
                name="basket_item_count"
                onChange={e => countChange(e.target.value)}
                defaultValue={count}
              >
                {Array.from({ length: 999 }, (v, k) => k + 1).map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="controls">
            {signCode && editable && (
              <a className="edit_sign_link button button-subtle mb-0" onClick={() => editLink(t, item)}>
                <EditOutline />
                {t("misc.edit")}
              </a>
            )}
            <a className="basket_remove_button button button-subtle icon-only mb-0" onClick={deleteItem}>
              <TrashOutline />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

const BasketSignItemDesktop: React.FC<BasketSignItemWrappedProps> = ({
  even,
  index,
  item: itemFragment,
  deleteItem,
  loading,
  countLoading,
  count,
  countOp,
  countChange,
  rowRef,
  openPreview,
  signCode,
  name,
  svgs,
  currentCount,
  editable,
  bundleItem,
}) => {
  const item = getFragment(BasketItemFragmentDoc, itemFragment)
  const { t } = useTranslation()
  const switchBackground =
    (item.signType === "vinyl_text" || item.signType === "contour_cut") && item.colors === "White"
  const bundleSvg = bundleItem && item.previewSvg

  return (
    <tr
      key={item.id}
      className={cn("item", "product-row", "desktop", {
        loading,
        even,
      })}
      ref={rowRef}
    >
      <td className="index">{index}</td>
      {(signCode || bundleSvg) && (
        <td className={cn("sign", { "bg-white-10": switchBackground })}>
          <div
            className="background-preview"
            onClick={() => {
              openPreview({
                svg: svgs,
                transparent: true,
                withBackground: true,
                signWidth: item.width || 0,
                signHeight: item.height || 0,
                signType: item.signType || "",
                metaProduct: item.metaProduct || "",
              })
            }}
          >
            <EyeOpenOutline />
          </div>
          <span
            onClick={() =>
              openPreview({
                svg: svgs,
                transparent: true,
                withBackground: true,
                signWidth: item.width || 0,
                signHeight: item.height || 0,
                signType: item.signType || "",
                metaProduct: item.metaProduct || "",
              })
            }
          >
            {item.previewSvgB ? (
              <>
                <SVGInline component="div" svg={item.previewSvg || ""} className="svg-wrapper data-hj-suppress" />
                <SVGInline component="div" svg={item.previewSvgB || ""} className="svg-wrapper data-hj-suppress" />
              </>
            ) : (
              <SVGInline component="div" svg={item.previewSvg || ""} className="svg-wrapper data-hj-suppress" />
            )}
          </span>
        </td>
      )}
      {bundleItem && !bundleSvg && (
        <td className="flex justify-center">
          {item.bundle?.image?.png ? (
            <div className="w-[110px]">
              <img src={item.bundle.image.png} className="p-2" />
            </div>
          ) : (
            <BundleOutline className="h-16 w-16" />
          )}
        </td>
      )}
      {!(signCode || bundleItem) && <td className="sign" />}
      <td className="name">
        {name}
        {item.previewSvgB && (
          <>
            <br />
            {item.materialTr}
          </>
        )}
      </td>
      <td className="size basket_size">{signCode || item.customProduct ? `${size(item)} ${sizeUnit}` : ""}</td>
      <td className="fixmethod">{fixmethodName(item as BasketItem, t)}</td>
      <td className="color">{item.colors}</td>
      <td className="price">{ClientData.businessRole ? item.localPrice : item.localTaxedPrice}</td>
      <td className="count">
        <button
          className="spinners spinner_minus"
          disabled={currentCount < 2 || countLoading}
          onClick={() => countOp("minus")}
          type="button"
        >
          -
        </button>
        <input
          type="text"
          name="basket_item_count"
          className="article_count"
          value={count}
          onChange={e => countChange(e.target.value)}
          disabled={countLoading}
        />
        <button className="spinners spinner_plus" type="button" disabled={countLoading} onClick={() => countOp("plus")}>
          +
        </button>
      </td>
      <td className="total">
        <div className="paragraph-sm current mb-0 text-right font-semibold">
          {ClientData.businessRole ? item.localTotal : item.localTaxedTotal}
        </div>
        {item.hasDiscount && (
          <div className="regular text-right text-xs text-ink-50 line-through">
            {ClientData.businessRole ? item.regularLocalTotal : item.regularTaxedTotal}
          </div>
        )}
      </td>
      <td className="item-controls">
        <div className="item-buttons">
          {signCode && editable && (
            <a className="edit_sign_link" onClick={() => editLink(t, item)}>
              <EditFill />
              {t("misc.edit")}
            </a>
          )}
          <a className="basket_remove_button" onClick={deleteItem}>
            <TrashFill />
            {t("misc.delete")}
          </a>
        </div>
      </td>
    </tr>
  )
}

const BasketSignItem: React.FC<BasketSignItemProps> = props => {
  const item = getFragment(BasketItemFragmentDoc, props.item)
  const { t } = useTranslation()
  const [count, setCount] = useState(item.totalCount.toString())
  const admin = !!(ClientData.admin && ClientData.admin.id)

  const [countMutation, countMutationResult] = useMutation(countUpdateDoc, {
    onCompleted: data => {
      if (data && data.basketCountUpdate && data.basketCountUpdate.item) {
        setCount(data.basketCountUpdate.item.totalCount.toString())
      }
    },
  })

  const [deleteMutation, deleteMutationResult] = useMutation(deleteDoc, {
    variables: { id: item.id },
  })

  const debouncedCountMutation = useRef(debounce(countMutation, 300, { leading: false }))

  const countOp = useCallback(
    (op: "plus" | "minus") => {
      const current = parseInt(count, 0) || 0
      const newCount = op === "plus" ? current + 1 : current - 1
      setCount(newCount.toString())
      debouncedCountMutation.current({ variables: { id: item.id, count: newCount } })
    },
    [count, setCount, item.id]
  )

  const countChange = useCallback(
    (value: string) => {
      const newCount = parseInt(value)

      if (!isNaN(newCount)) {
        setCount(newCount.toString())
        debouncedCountMutation.current({
          variables: { id: item.id, count: Math.max(Math.min(newCount, 10000), 1) },
        })
      }
    },
    [setCount, item.id]
  )

  const rowRef = useRef<HTMLDivElement & HTMLTableRowElement>(null)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    if (rowRef.current && props.scrollIntoView && !scrolled) {
      setScrolled(true)
      setTimeout(() => rowRef.current && rowRef.current.scrollIntoView({ block: "nearest" }), 50)
    }
  }, [props.scrollIntoView, scrolled, setScrolled])

  const [openPreview, previewProps] = useSignPreview()

  const currentCount = parseInt(count)
  const signCode = item.articleType === "sign_code"
  const bundleItem = item.articleType === "bundle_item"

  const name = signCode
    ? item.metaProduct
      ? t(`sign_type.${item.metaProduct}`)
      : t(`sign_type.${item.signType}`)
    : item.clientDescription

  const svgs: string[] = []

  if (item.previewSvg) {
    svgs.push(item.previewSvg)
  }

  if (item.previewSvgB) {
    svgs.push(item.previewSvgB)
  }

  const itemProps = {
    ...props,
    deleteItem: deleteMutation,
    loading: deleteMutationResult.loading,
    countLoading: countMutationResult.loading,
    count,
    countOp,
    countChange,
    rowRef,
    openPreview,
    signCode,
    name,
    svgs,
    currentCount,
    editable: item.customerEdit || admin,
    bundleItem,
  }

  return (
    <>
      {props.desktop ? <BasketSignItemDesktop {...itemProps} /> : <BasketSignItemMobile {...itemProps} />}
      <SignPreview {...previewProps} />
    </>
  )
}

export default BasketSignItem
